import { useCallback, useState } from "react"

import { useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { PersonalPolicyPush } from "@/api/admin-api"

import { pushPersonalSpacesPolicy } from "@/api/admin-api"
import { getApiErrorMessage } from "@/lib/api-error-message"

export type PersonalPolicyConfirmationState = {
  open: boolean
  /** The number of users FILTH says the change hides; null when FILTH did not say. */
  expectedHidden: number | null
  /** The policy snapshot FILTH holds back; a confirmation covers exactly this one. */
  policyDigest: string | null
  pending: boolean
  /** True after a confirmation that FILTH refused because the number moved. */
  countChanged: boolean
  /** True after a confirmation that was not applied because the policy itself changed (same number). */
  policyChanged: boolean
}

/**
 * Surfaces what happened to a policy push after an admin saved a personal-source
 * change, and carries the admin's explicit confirmation when FILTH's mass-purge
 * guard holds it (spec 3.7, invariant 43).
 *
 * `reportPush` is called with the `policy_push` of any save. `applied` is silent;
 * `confirmation_required` opens the dialog; everything else is said honestly in a
 * toast, because FILTH is still enforcing the previous policy. A confirmation is only
 * ever sent for the exact count and the exact policy snapshot FILTH named, and never on
 * a save's behalf.
 */
export function usePersonalPolicyConfirmation() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [state, setState] = useState<PersonalPolicyConfirmationState>({
    open: false,
    expectedHidden: null,
    policyDigest: null,
    pending: false,
    countChanged: false,
    policyChanged: false,
  })

  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["admin", "personalSpaces"] })
    void queryClient.invalidateQueries({ queryKey: ["admin", "features"] })
  }, [queryClient])

  const reportPush = useCallback(
    (push: PersonalPolicyPush | undefined) => {
      if (!push) return
      if (push.status === "applied") {
        refresh()
        return
      }
      if (push.status === "confirmation_required") {
        setState({
          open: true,
          expectedHidden: push.expected_hidden ?? null,
          policyDigest: push.policy_digest ?? null,
          pending: false,
          countChanged: false,
          policyChanged: false,
        })
        return
      }
      toast.warning(
        t(
          "adminDashboard.personalSources.policy.notApplied",
          "Saved, but Prism has not applied the change yet. It keeps enforcing the previous settings."
        )
      )
    },
    [refresh, t]
  )

  const dismiss = useCallback(() => {
    setState((current) => (current.pending ? current : { ...current, open: false }))
    refresh()
  }, [refresh])

  const confirm = useCallback(async () => {
    const { expectedHidden, policyDigest } = state
    if (expectedHidden === null || !policyDigest) return
    setState((current) => ({ ...current, pending: true }))
    try {
      const { policy_push: push } = await pushPersonalSpacesPolicy({ expectedHidden, policyDigest })
      if (push.status === "applied") {
        setState((current) => ({ ...current, open: false, pending: false }))
        toast.success(
          t("adminDashboard.personalSources.policy.confirmed", "The change has been applied")
        )
        refresh()
      } else if (push.status === "confirmation_required") {
        const nextHidden = push.expected_hidden ?? null
        const nextDigest = push.policy_digest ?? null
        const countChanged = nextHidden !== expectedHidden
        setState({
          open: true,
          expectedHidden: nextHidden,
          policyDigest: nextDigest,
          pending: false,
          countChanged,
          // Same number, another policy: say that the policy changed, not that the number did.
          policyChanged: !countChanged && nextDigest !== policyDigest,
        })
      } else {
        setState((current) => ({ ...current, open: false, pending: false }))
        toast.warning(
          t(
            "adminDashboard.personalSources.policy.notApplied",
            "Saved, but Prism has not applied the change yet. It keeps enforcing the previous settings."
          )
        )
      }
    } catch (error) {
      setState((current) => ({ ...current, pending: false }))
      toast.error(
        getApiErrorMessage(
          error,
          t(
            "adminDashboard.personalSources.policy.confirmFailed",
            "The change could not be applied"
          )
        )
      )
    }
  }, [refresh, state, t])

  return { state, reportPush, confirm, dismiss }
}
